import store from '@/store'
import { Action, config, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { VIEWPOINT_API } from "@/api/ViewpointApi";
import { ModelDefinition } from "@/api/model/ModelDefinition";
import { FactorGroup, FactorResource } from "@/api/model/FactorResource";
import _ from "lodash";
import Vue from "vue";
import Decisions from "@/store/modules/Decisions";
import WorkspaceOptions from './WorkspaceOptions';
import { FactorList, FactorListItem } from '@/api/model/ResourceList';

config.rawError = true

const FACTOR_RESOURCE_COMP_FIELDS = [
    'name', 'description', 'numWeightingDots', 'dataField',
]
const FACTOR_GROUP_COMP_FIELDS = [
    'groupTypeDefinition', 'summaryScoringFunction',
    'scoringKeyDefinition', 'childrenIds', 'hideScores'
]

const FACTOR_COMP_FIELDS = [
    'valueTypeDefinition', 'scoringDefinition',
    'calculationDefinition', 'constraint'
]

const name = 'modelling';
if (module.hot) {
    if (store.hasModule(name)) {
        store.unregisterModule(name);
    }
}

@Module({dynamic: true, store, name: name, namespaced: true})
export default class Modelling extends VuexModule {

    updating: boolean = false;

    publishedDecisionModelDefinition: ModelDefinition | null = null;
    workingDecisionModelDefinition: ModelDefinition | null = null;

    publishedModelFactorResources: { [id: string]: FactorResource } = {};
    publishedModelSummaryFactorResources: { [id: string]: FactorResource } = {};
    workingModelFactorResources: { [id: string]: FactorResource } = {};
    sandboxModelFactorResources: { [id: string]: FactorResource } = {};
    copyFactorResource: FactorResource | null = null;

    isSandbox: boolean = false;

    get workingModelFactorNames() {
        return Object.entries(this.workingModelFactorResources).map((item) => {
            return item[1].name;
        })
    }

    @Mutation
    clear() {
        this.publishedDecisionModelDefinition = null;
        this.workingDecisionModelDefinition = null;
        this.publishedModelFactorResources = {};
        this.publishedModelSummaryFactorResources = {};
        this.workingModelFactorResources = {};
        this.copyFactorResource = null;
    }

    @Mutation
    setUpdating(updating: boolean) {
        this.updating = updating;
    }


    @Mutation
    setPublishedDecisionModelDefinition(modelDefinition: ModelDefinition) {
        this.publishedDecisionModelDefinition = modelDefinition;
    }

    @Mutation
    setWorkingDecisionModelDefinition(modelDefinition: ModelDefinition) {
        this.workingDecisionModelDefinition = modelDefinition;
    }

    @Mutation
    setPublishedModelFactorResource(factorResource: FactorResource) {
        if (!factorResource.id) return;
        Vue.set(this.publishedModelFactorResources, factorResource.id, factorResource);
    }

    @Mutation
    setPublishedModelSummaryFactorResource(factorResource: "M") {
        Vue.set(this.publishedModelSummaryFactorResources, factorResource, factorResource);
    }

    @Mutation
    setPublishedModelFactorResources(payload: { [id: string]: FactorResource }) {
        this.publishedModelFactorResources = payload;
    }

    @Mutation
    setPublishedModelSummaryFactorResources(payload: { [id: string]: FactorResource }) {
        this.publishedModelSummaryFactorResources = payload;
    }

    @Mutation
    setWorkingModelFactorResources(payload: { [id: string]: FactorResource }) {
        this.workingModelFactorResources = payload;
    }

    @Mutation
    setWorkingModelFactorResource(factorResource: FactorResource) {
        if (!factorResource.id) return;
        Vue.set(this.workingModelFactorResources, factorResource.id, factorResource);
    }

    @Mutation
    setWorkingModelFactorResourcesSet(factorResources: FactorResource[]) {
        factorResources.forEach(factorResource => Vue.set(this.workingModelFactorResources, factorResource.id, factorResource))
    }

    @Mutation
    deleteWorkingModelFactorResource(factorResourceId: string) {
        Vue.delete(this.workingModelFactorResources, factorResourceId);
    }

    @Mutation
    setCopyFactorResource(factorResource: FactorResource | null) {
        this.copyFactorResource = factorResource;
    }

    @Mutation
    setIsSandbox(value: boolean) {
        if(value) {
            this.sandboxModelFactorResources = _.cloneDeep(this.publishedModelFactorResources);
        }
        this.isSandbox = value;
    }


    @Action
    async getPublishedDecisionModel(decisionId: string) {
        let modelDefinition = await VIEWPOINT_API.getDecisionModel(decisionId, 'published')
        let modelResources = await VIEWPOINT_API.getModelFactorResources(modelDefinition.modelId, modelDefinition.revision)
        this.setPublishedModelFactorResources(_.keyBy(modelResources, 'id'))
        this.setPublishedDecisionModelDefinition(modelDefinition);
        return modelDefinition;
    }

    @Action
    async getWorkingDecisionModel(decisionId: string) {
        let modelDefinition = await VIEWPOINT_API.getDecisionModel(decisionId)
        let modelResources = await VIEWPOINT_API.getModelFactorResources(modelDefinition.modelId, modelDefinition.revision)
        this.setWorkingModelFactorResources(_.keyBy(modelResources, 'id'))
        this.setWorkingDecisionModelDefinition(modelDefinition);
        return modelDefinition;
    }

    @Action
    async createFactorResource(payload: { parentId: string, factorResource: FactorResource }) {
        let parentFactorResource = this.workingModelFactorResources[payload.parentId] as FactorGroup;
        let modelDefinition = this.workingDecisionModelDefinition;
        if (!parentFactorResource || !modelDefinition) return;

        this.setUpdating(true)
        let newFactorResources = await VIEWPOINT_API.createFactorResourceInGroup(modelDefinition.modelId, modelDefinition.revision, payload.parentId, payload.factorResource);
        newFactorResources.forEach(this.setWorkingModelFactorResource);

        if(!this.isSandbox) {
            this.publishModel();
            newFactorResources.forEach(this.setPublishedModelFactorResource);
            if (getModule(WorkspaceOptions).activeFactorLists.length > 0 && getModule(WorkspaceOptions).addFactorToList) {
                getModule(Decisions).addFactorToActiveLists(newFactorResources[0].id);
            }
        }
        this.setUpdating(false);
        return newFactorResources;
    }

    @Action
    async createFactorResources(factorResources: FactorResource[]) {
        let workspaceOptionsModule = getModule(WorkspaceOptions);
        let modelDefinition = this.workingDecisionModelDefinition;
        if (!modelDefinition) return;

        this.setUpdating(true)
        let newFactorResources = await VIEWPOINT_API.createFactorResources(modelDefinition.modelId, modelDefinition.revision, factorResources);
        newFactorResources.forEach(this.setWorkingModelFactorResource)
        this.setUpdating(false)
        if(!this.isSandbox) {
            this.publishModel();
            if (workspaceOptionsModule.activeFactorLists.length > 0 && getModule(WorkspaceOptions).addFactorToList) {
                getModule(Decisions).addFactorsToSelectLists({factors: newFactorResources, listIds: workspaceOptionsModule.activeFactorLists});
            }
        }
        return newFactorResources;
    }

    @Action
    async deleteFactorResources(factorResourceIds: string[]) {
        if (!this.workingDecisionModelDefinition) return;
        let factorGroups = Object.values(this.workingModelFactorResources)
            .filter(f => f.type === 'FACTOR_GROUP')
            .map(f => f as FactorGroup)
            .filter(fg => fg.childrenIds && _.intersection(fg.childrenIds, factorResourceIds).length > 0)

        factorGroups.forEach(row => row.childrenIds = _.without(row.childrenIds, ...factorResourceIds))
        this.setUpdating(true)
        if (factorGroups) {
            let updatedFactorGroups = await VIEWPOINT_API.updateFactorResources(this.workingDecisionModelDefinition.modelId, this.workingDecisionModelDefinition.revision, factorGroups)
            this.setWorkingModelFactorResourcesSet(updatedFactorGroups)
        }
        factorResourceIds.forEach(id => this.deleteWorkingModelFactorResource(id));
        this.setUpdating(false)
    }

    @Action
    async deleteFactorResource(factorResourceId: string) {
        if (!this.workingDecisionModelDefinition) return;
        let factorGroups = Object.values(this.workingModelFactorResources)
            .filter(f => f.type === 'FACTOR_GROUP')
            .map(f => f as FactorGroup)
            .filter(fg => fg.childrenIds && fg.childrenIds.includes(factorResourceId))

        factorGroups.forEach(fg => fg.childrenIds.splice(fg.childrenIds.indexOf(factorResourceId), 1))

        this.setUpdating(true)
        if (factorGroups) {
            let updatedFactorGroups = await VIEWPOINT_API.updateFactorResources(this.workingDecisionModelDefinition.modelId, this.workingDecisionModelDefinition.revision, factorGroups)
            this.setWorkingModelFactorResourcesSet(updatedFactorGroups)
        }
        this.deleteWorkingModelFactorResource(factorResourceId)
        if(!this.isSandbox) {
            this.publishModel()
        }
        this.setUpdating(false)
    }

    @Action
    async updateFactorResource(factorResource: FactorResource) {
        let modelDefinition = this.workingDecisionModelDefinition;
        if (!modelDefinition) return;
        this.setUpdating(true)
        let updatedFactorResource = await VIEWPOINT_API.updateFactorResource(modelDefinition.modelId, modelDefinition.revision, factorResource)
        this.setUpdating(false)
        this.setWorkingModelFactorResource(updatedFactorResource);
        if (!this.isSandbox)
            this.publishModel()
        return updatedFactorResource;
    }

    @Action
    async updateFactorResources(factorsToBeUpdated: FactorResource[]) {
        let modelDefinition = this.workingDecisionModelDefinition;
        if (!modelDefinition) return;
        this.setUpdating(true)
        let updatedFactorResources = await VIEWPOINT_API.updateFactorResources(modelDefinition.modelId, modelDefinition.revision, factorsToBeUpdated)
        this.setWorkingModelFactorResourcesSet(updatedFactorResources)
        this.setUpdating(false)
        if (!this.isSandbox)
            this.publishModel()
        return updatedFactorResources;
    }

    @Action
    async createFactorsFromList(payload: {factors: FactorResource[], destinationFactorResourceId: string}) {
        let factors = payload.factors;
        let destinationFactorResourceId = payload.destinationFactorResourceId
        if(factors.length > 0  && destinationFactorResourceId ) {
            let result = await this.createFactorResources(factors)
            let ids = result.map(val => { return val.id })
            let destinationResource = this.workingModelFactorResources[destinationFactorResourceId]
            if(destinationResource?.childrenIds) {
                destinationResource.childrenIds.push(...ids)
                this.updateFactorResource(destinationResource)
                this.setUpdating(false)
            }   else {
                Vue.set(destinationResource, 'childrenIds', ids)
                this.updateFactorResource(destinationResource)
                this.setUpdating(false)
            }
        }

    }

    @Action
    async replaceModelResources() {
        let modelDefinition = this.workingDecisionModelDefinition;
        if (!modelDefinition) return;
        
        this.setUpdating(true)
        await VIEWPOINT_API.replaceModelResources(modelDefinition.modelId, modelDefinition.revision, Object.values(this.workingModelFactorResources))
        this.setUpdating(false)
    }

    @Action
    async discardSandboxChanges() {
        let modelDefinition = this.workingDecisionModelDefinition;
        if (!modelDefinition) return;
        this.setUpdating(true)
        await VIEWPOINT_API.replaceModelResources(modelDefinition.modelId, modelDefinition.revision, Object.values(this.publishedModelFactorResources))
        await this.getWorkingDecisionModel(modelDefinition.modelId)
        this.setUpdating(false)
    }


    @Action
    async copySubTree(factorResource: FactorResource | null) {
        this.setCopyFactorResource(factorResource)
    }

    @Action
    async pasteFactorNameMatches( payload: {factorName: string, factorModel: String[]} ) {
        return payload.factorModel.filter((item) => {
            return item.substring(0, (payload.factorName.length)) == payload.factorName;
        })
    }

    @Action
    async pasteFactorName( payload: {targetFactor: FactorResource, factorModel: String[]} ) {
        let nameMatches = await this.pasteFactorNameMatches({factorName: payload.targetFactor['name'], factorModel: payload.factorModel})
        if (nameMatches.length > 0) {
            let index = 1;
            while (nameMatches.includes(payload.targetFactor.name + '-' + index)) {
                index ++;
            }
            return payload.targetFactor.name + '-' + index;
        }
        
        return payload.targetFactor.name
    }

    @Action
    async pasteFactorResourceSubTree(payload: { destinationId: string, destinationIndex?: number, factorResource: FactorResource }) {
        
        let modelDefinition = this.workingDecisionModelDefinition;
        if (!modelDefinition) return;
        let factorResource = payload.factorResource;
        let destinationFactorResource = this.workingModelFactorResources[payload.destinationId] as FactorGroup;

        let copy: FactorResource = _.cloneDeep(factorResource);
        delete copy.id

        let factorResources = [copy]
        
        factorResources[0].name = await this.pasteFactorName({ targetFactor: payload.factorResource, factorModel: this.workingModelFactorNames });
        if (copy.type === 'FACTOR_GROUP' && (copy as FactorGroup).childrenIds) {
            let childrenFactorResources = Modelling.getChildrenFactorResources((copy as FactorGroup).childrenIds, this.workingModelFactorResources)
            factorResources.push(..._.cloneDeep(childrenFactorResources.filter(f => f)));
        }
        factorResources.forEach(f => delete f.dataField)
        factorResources = _.uniq(factorResources)
        this.setUpdating(true)
        let createdFactorResources = await VIEWPOINT_API.createFactorResources(modelDefinition.modelId, modelDefinition.revision, factorResources);
        let topResource = createdFactorResources[0]
        if (!topResource.id) {
            this.setUpdating(false)
            return;
        }
        destinationFactorResource.childrenIds.splice(payload.destinationIndex != null ? payload.destinationIndex : destinationFactorResource.childrenIds.length, 0, topResource.id);
        let updatedFactorResource = await VIEWPOINT_API.updateFactorResource(modelDefinition.modelId, modelDefinition.revision, destinationFactorResource);
        this.setWorkingModelFactorResourcesSet([...createdFactorResources, updatedFactorResource]);
        this.setUpdating(false)
        if(!this.isSandbox) {
            await this.publishModel();
            let workspaceOptionsModule = getModule(WorkspaceOptions)
            if (workspaceOptionsModule.activeFactorLists.length > 0 && workspaceOptionsModule.addFactorToList) {
                getModule(Decisions).addFactorsToSelectLists({ factors: createdFactorResources, listIds: workspaceOptionsModule.activeFactorLists });
            }
        }
    }

    @Action
    async crossDecisionPasteFactorResource(payload: { destinationDecision: string, destinationId: string, destinationIndex?: number, factorResource: FactorResource }) {
        let outputDecisionDefinition = await VIEWPOINT_API.getDecisionModel(payload.destinationDecision)
        let result = await VIEWPOINT_API.getModelFactorResources(outputDecisionDefinition.modelId, outputDecisionDefinition.revision)
        let outputModelResources = _.keyBy(result, 'id')

        if (!outputDecisionDefinition) return;
        let factorResource = payload.factorResource;
        let destinationFactorResource = outputModelResources['M'] as FactorGroup;

        let copy: FactorResource = _.cloneDeep(factorResource);
        delete copy.id

        let factorResources = [copy]
        
        factorResources[0].name = await this.pasteFactorName({ targetFactor: payload.factorResource, factorModel: Object.entries(result).map((item) => {
            return item[1].name;
        })});

        if (copy.type === 'FACTOR_GROUP' && (copy as FactorGroup).childrenIds) {
            let childrenFactorResources = Modelling.getChildrenFactorResources((copy as FactorGroup).childrenIds, this.workingModelFactorResources)
            factorResources.push(..._.cloneDeep(childrenFactorResources.filter(f => f)));
        }
        factorResources.forEach(f => delete f.dataField)
        factorResources = _.uniq(factorResources)
        this.setUpdating(true)
        let createdFactorResources = await VIEWPOINT_API.createFactorResources(outputDecisionDefinition.modelId, outputDecisionDefinition.revision, factorResources);
        let topResource = createdFactorResources[0]
        if (!topResource.id) {
            this.setUpdating(false)
            return;
        }
        destinationFactorResource.childrenIds.splice(payload.destinationIndex != null ? payload.destinationIndex : destinationFactorResource.childrenIds.length, 0, topResource.id);
        let updatedFactorResource = await VIEWPOINT_API.updateFactorResource(outputDecisionDefinition.modelId, outputDecisionDefinition.revision, destinationFactorResource);
        // With new model builder changes, need to publish the output decision so that the changes aren't cleared
        await VIEWPOINT_API.publishModel(outputDecisionDefinition.modelId)

        this.setUpdating(false)
    }

    //Copy into new decision and return mapping
    @Action
    async pasteNewDecision(payload: { destinationDecision: string, destinationId: string, destinationIndex?: number, factorResource: FactorResource }) {
        let outputDecisionDefinition = await VIEWPOINT_API.getDecisionModel(payload.destinationDecision)
        let result = await VIEWPOINT_API.getModelFactorResources(outputDecisionDefinition.modelId, outputDecisionDefinition.revision)
        let outputModelResources = _.keyBy(result, 'id')

        if (!outputDecisionDefinition) return;
        let factorResource = payload.factorResource;
        let destinationFactorResource = outputModelResources['M'] as FactorGroup;

        let copy: FactorResource = _.cloneDeep(factorResource);
        delete copy.id
        //Get all children of root element
        let factorResources = [copy]
        if (copy.type === 'FACTOR_GROUP' && (copy as FactorGroup).childrenIds) {
            let childrenFactorResources = Modelling.getChildrenFactorResources((copy as FactorGroup).childrenIds, this.workingModelFactorResources)
            factorResources.push(..._.cloneDeep(childrenFactorResources.filter(f => f)));
        }
        //Remove all id's so that the backend can create 
    
        factorResources = factorResources.filter(factor => factor.id != 'M')
        factorResources.forEach(f => delete f.dataField)
        factorResources = _.uniq(factorResources)
        this.setUpdating(true)
        //let result2 = await VIEWPOINT_API.replaceModelResources(outputDecisionDefinition.modelId, outputDecisionDefinition.revision, [destinationFactorResource])

        let createdFactorResources = await VIEWPOINT_API.createFactorResources(outputDecisionDefinition.modelId, outputDecisionDefinition.revision, factorResources);
        //console.log(result2, createdFactorResources, factorResources)
        let topResource = createdFactorResources[0]
        if (!topResource.id) {
            this.setUpdating(false)
            return;
        }
        destinationFactorResource.childrenIds.splice(payload.destinationIndex != null ? payload.destinationIndex : destinationFactorResource.childrenIds.length, 0, ...topResource.childrenIds);
        destinationFactorResource.childrenIds = destinationFactorResource.childrenIds.filter((fId: string) => fId != 'FR1' && fId != 'FR3')
        let updatedFactorResource = await VIEWPOINT_API.updateFactorResource(outputDecisionDefinition.modelId, outputDecisionDefinition.revision, destinationFactorResource);
        
        //await VIEWPOINT_API.replaceModelResources(outputDecisionDefinition.modelId, outputDecisionDefinition.revision, createdFactorResources)
        await VIEWPOINT_API.publishModel(outputDecisionDefinition.modelId)
        let map = [];
        copy.childrenIds.map((id: string, index) => Vue.set(map, id, destinationFactorResource.childrenIds[index]))
        console.log(map)
        this.setUpdating(false)
        return map
    }
    


    @Action
    async publishModel() {
        let modelDefinition = this.workingDecisionModelDefinition;
        if (!modelDefinition) return;
        this.setUpdating(true)
        let newWorkingModelDefinition = await VIEWPOINT_API.publishModel(modelDefinition.modelId)
        this.setWorkingDecisionModelDefinition(newWorkingModelDefinition)
        let modelResources = await VIEWPOINT_API.getModelFactorResources(modelDefinition.modelId, modelDefinition.revision)
        this.setWorkingModelFactorResources(_.keyBy(modelResources, 'id'))
        let selectedDecision = getModule(Decisions).selectedDecision;
        if (selectedDecision) {
            await this.getPublishedDecisionModel(selectedDecision.decisionId)
        }
        // kathy - delete factors in the listManager after publishing the model
        getModule(Decisions).deleteFactorFromList();
        this.setUpdating(false)

    }

    static getChildrenFactorResources(childrenIds: string[], factorResources: { [id: string]: FactorResource }): FactorResource[] {
        let childrenFactorResources = []
        for (let id of childrenIds) {
            let child = factorResources[id]
            if (child && child.type === 'FACTOR_GROUP' && (child as FactorGroup).childrenIds) {
                let childrenFactors = Modelling.getChildrenFactorResources((child as FactorGroup).childrenIds, factorResources);
                childrenFactorResources.push(...childrenFactors)
            }
            childrenFactorResources.push(child);
        }
        return childrenFactorResources;
    }

    @Action
    async checkDiff() {
        if(this.modelDiff.isSame) {
            return
        } else {
            await this.discardSandboxChanges()
        }
    }

    /**
     * Returns the differences between the working and published model
     */
    get modelDiff() {
        if (!this.workingModelFactorResources || !this.publishedModelFactorResources) return {};
        let additions = [];
        let deletions = [];
        let updates: any = {};

        for (let publishedFactor of Object.values(this.publishedModelFactorResources) as any) {
            if (!this.workingModelFactorResources[publishedFactor.id]) {
                deletions.push(publishedFactor.id)
            } else {
                let draftFactor = this.workingModelFactorResources[publishedFactor.id] as any
                let factorUpdates = [];
                for (let field of FACTOR_RESOURCE_COMP_FIELDS) {
                    if (!_.isEqual(draftFactor[field], publishedFactor[field])) factorUpdates.push(field)
                }
                if (publishedFactor.type === 'FACTOR') {
                    for (let field of FACTOR_COMP_FIELDS) {
                        if (!_.isEqual(draftFactor[field], publishedFactor[field])) factorUpdates.push(field)
                    }
                }
                if (publishedFactor.type === 'FACTOR_GROUP') {
                    for (let field of FACTOR_GROUP_COMP_FIELDS) {
                        if (!_.isEqual(draftFactor[field], publishedFactor[field])) factorUpdates.push(field)
                    }
                }
                if (factorUpdates.length > 0) updates[publishedFactor.id] = factorUpdates;
            }
        }
        let factorAdditions = _.without(Object.keys(this.workingModelFactorResources), ...Object.keys(this.publishedModelFactorResources))
        additions.push(...factorAdditions);
        return {
            isSame: additions.length === 0 && deletions.length === 0 && Object.keys(updates).length === 0,
            additions: additions,
            deletions: deletions,
            updates: updates
        };
    }

    get factorListTree() {
        return Modelling.getListFactors(this.publishedModelFactorResources)
    }
   

    private static getListFactors(factorResources) {
        const workspaceOptions = getModule(WorkspaceOptions)
        let factorList = workspaceOptions.activeFactorLists;
        // console.log(factorList)
        let allLists = getModule(Decisions).decisionLists
        let factorIds: string[] = [];
        let factorLists = allLists as { [listId: string]: FactorList };
        if (factorLists && factorList.length) {
            for (let id of Object.values(factorList)) {
                if (factorLists[id]) {
                    factorIds = factorIds.concat(factorLists[id].factors.map((id: FactorListItem) => id.factorId as string))
                } else {
                    continue;
                }

            }

            factorIds = _.uniq(factorIds);
            // console.log(factorIds)
            let path = []
            for(let factor of factorIds) {
                path = path.concat(this.findBranch(factor, factorResources, factorResources['M']))

            }
            path = _.uniq(path)
            // console.log(path)
            if(!path.includes(workspaceOptions.selectedPathItemId)) {
                workspaceOptions.selectBreadcrumbItem('M')
            }
            return _.pick(factorResources,path)
        }
    }








    

    private static findBranch(searchingId: string, factorResources: any, factorResource: FactorResource): string[] | undefined {
        if (factorResource.id === searchingId) return [searchingId];
        if (factorResource.type !== 'FACTOR_GROUP') return undefined;
        let factorGroup = factorResource as FactorGroup;
        for (let childId of factorGroup.childrenIds) {
            let childPath = this.findBranch(searchingId, factorResources, factorResources[childId]);
            if (childPath) {
                return [factorResource.id, ...childPath];
            }
        }
    }

}
